import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { applyPartialConversionColumnName } from '@bingads-webui-campaign/column-conversions';

import { getlandingGridColumnConfig } from './landing-grid-column-config';
import { Constants } from '../constants';
import { columnConfigToColumnChooserConfig, isConversionsColumnAllowed } from '../column-config-to-chooser-config';
import { updateSovColumns } from '../shared/util';

export function getLandingGridConfig({
  i18n,
  jsDataCampaign,
  edm,
  globalConfig,
  landingSchema,
  getCurrentLevel,
  deleteSchema,
  bingadsGridColumn,
  gridType,
  isForVNext,
}) {
  const permissions = globalConfig.get('Permissions') || {};
  const {
    IsViewThroughConversionEnabled,
    IsAvgPositionDeprecationPhase1Enabled,
    IsAvgPositionDeprecationPhase2Enabled,
    // IsViewThroughConversionsRevenueEnabled,
  } = permissions;
  // (youngim) MT bug needs to be fixed for dimensinos
  const IsViewThroughConversionsRevenueEnabled = false;
  const landingColumnConfig = getlandingGridColumnConfig({ i18n, bingadsGridColumn, globalConfig });
  const level = getCurrentLevel();
  return {
    getColumns: () => landingColumnConfig.getLevelOfDetailColumns(level)
      .concat(landingColumnConfig.getPerfColumns())
      .concat(landingColumnConfig.getConversionColumns()),
    getColumnChooserConfiguration: () => {
      const lockedColumns = ['KeywordDestinationUrl', 'Impressions'];
      const columnChooserConfig = {
        categories: [{
          name: i18n.getString('LevelOfDetail'),
          options: columnConfigToColumnChooserConfig(
            landingColumnConfig.getLevelOfDetailColumns(level),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Performance'),
          options: columnConfigToColumnChooserConfig(
            landingColumnConfig.getPerfColumns(),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Conversions'),
          options: columnConfigToColumnChooserConfig(
            _.filter(
              landingColumnConfig.getConversionColumns(),
              ({ name }) => isConversionsColumnAllowed({
                name,
                IsViewThroughConversionEnabled,
                IsViewThroughConversionsRevenueEnabled,
              })
            ),
            lockedColumns
          ),
        }],
        selected: lockedColumns.concat(applyPartialConversionColumnName(permissions, _.compact([
          'KeywordDestinationUrl',
          'FinalUrl',
          'Impressions',
          'Clicks',
          'CTR',
          'AverageCPC',
          'Spend',
          'Conversions',
          !IsAvgPositionDeprecationPhase1Enabled && !IsAvgPositionDeprecationPhase2Enabled && 'AveragePosition',
          IsAvgPositionDeprecationPhase1Enabled && 'TopImpressionRatePercent',
          IsAvgPositionDeprecationPhase1Enabled && 'AbsoluteTopImpressionRatePercent',
        ]))),
        mandatory: [],
        locked: lockedColumns,
        patchQuery: true,
        isForVNext,
      };

      if (isForVNext) {
        columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
      }

      return columnChooserConfig;
    },

    getEntity: () => jsDataCampaign.Time,

    getEntitySet: () => _.result(edm.currentAccount, 'Time'),

    getSchema: () => {
      const columns = ['CampaignName', 'AdGroupName'];
      const performanceMetricsProperty = landingSchema.properties.PerformanceMetrics;
      const allConvMetrics = applyPartialConversionColumnName(permissions, [
        'AllConversionCPA',
        'AllConversions',
        'AllConversionRate',
        'AllConversionAdvertiserReportedRevenue',
        'AllConversionRevenueOnAdSpend',
        'ViewThroughConversions',
        'ViewThroughConversionsRevenue',
        'ViewThroughConversionsCPA',
        'ViewThroughConversionsReturnOnAdSpend',
        'ViewThroughConversionsRate',
      ]);

      _.each(allConvMetrics, (allConvMetric) => {
        if (performanceMetricsProperty &&
            putil.get(performanceMetricsProperty.schema.properties, allConvMetric)) {
          landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
            .schema.properties[allConvMetric].filterable = true;
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversions')) {
            landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRevenue')) {
            landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsCPA')) {
            landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsReturnOnAdSpend')) {
            landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRate')) {
            landingSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
        }
      });

      deleteSchema(columns, landingSchema);

      if (level === Constants.AccountLevel) {
        landingSchema.properties.CampaignName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
        landingSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      } else if (level === Constants.CampaignLevel) {
        landingSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      }

      const sovColumnNames = {
        AbsoluteTopImpressionRate: 'AbsoluteTopImpressionRatePercent',
        TopImpressionRate: 'TopImpressionRatePercent',
      };

      updateSovColumns(landingSchema, sovColumnNames);

      return landingSchema;
    },
    getReportName: () => 'DestinationUrl',
    getReportSchema() {
      return _.defaults({
        name: this.getReportName(gridType),
      }, this.getSchema(gridType));
    },
    modifyFilter: filters => filters,
    getRewriteMap: () => ({
      KeywordDestinationUrl: i18n.getString('ColumnName_DimKeywordDestinationUrl'),
      FinalUrl: i18n.getString('ColumnName_DimFinalUrl'),
      UrlCustomParameters: i18n.getString('ColumnName_DimUrlCustomParameters'),
      TrackingTemplate: i18n.getString('ColumnName_TrackingTemplate'),
      FinalUrlSuffix: i18n.getString('ColumnName_FinalUrlSuffix'),
      AdGroupName: i18n.getString('FilterLocalization_DataAttribute_AdGroupName'),
      CampaignName: i18n.getString('FilterLocalization_DataAttribute_CampaignName'),
    }),
  };
}
